import { useContext, useEffect, useMemo, useState } from 'react'

import moment from 'moment'
import { useSelector } from 'react-redux'
import Cookie from 'js-cookie'

import Analytics from '../utils/analytics'
import {
  IN_INSTITUTION_STATE, getAssistantBirthdate, getAssistantEmail, getAssistantFirstname, getAssistantFullFinancialYearAbbreviation,
  getAssistantFullSpecialityAbbreviation, getAssistantLastname, getAssistantMatricule, getAssistantPhone, getEndDate, getInstitutionName,
  getSchoolName, getSpecialityAbbreviation, getStartDate, getWorkingRegimeName
} from '../utils/entities/maccsInternship'
import { INSTITUTION_MACCS_CONTEXT, LanguageLocales, OrderBy, SCHOOL_MACCS_CONTEXT } from '../utils/constants'
import { getAllMaccsInternships } from '../utils/api/maccsInternship'
import { exportMaccsInternshipsByPeriod, getDocument } from '../utils/api/export'
import { GlobalContext } from '../Providers/GlobalProvider'
import { selectTranslate } from '../reducers/LocaleReducer/LocalReducer'
import { TableParameters } from '../utils/entities/tableParameters'
import { Metadata, retrieveMetadata } from '../utils/http'
import useSessionStorage from '../hooks/UseSessionStorage'
import { DATE_FORMAT } from '../utils/momentjs'
import DataTable from '../Components/antd/Tables/DataTable/DataTable'
import { onError } from '../utils/apiHelper'

const SESSION_STORAGE_SEARCH_KEY = 'assistants-reporting-view.parameters'
const INITIAL_PERIOD = { startDate: moment().format(DATE_FORMAT), endDate: moment().add(4, 'weeks').format(DATE_FORMAT) }
const DEFAULT_PARAMETERS = { orderBy: OrderBy.EMAIL, pageSize: 50, period: INITIAL_PERIOD }

const AssistantsReportingView = () => {
  const { user } = useContext(GlobalContext)

  const t = useSelector(selectTranslate)

  const maxHeight = useMemo(() => `calc(100vh - ${Cookie.get('switch_user_id') ? '240px' : '199px'})`, [Cookie.get('switch_user_id')])

  const [data, setData] = useState({ data: [], meta: new Metadata({ pageSize: 50 }) })
  const [rawParameters, setRawParameters] = useSessionStorage(SESSION_STORAGE_SEARCH_KEY, new TableParameters({ ...DEFAULT_PARAMETERS }))
  const [loading, setLoading] = useState(false)

  const parameters = useMemo(() => new TableParameters({
    ...rawParameters,
    metadata: data.meta,
    period: { startDate: moment(rawParameters.period.startDate), endDate: moment(rawParameters.period.endDate) }
  }), [rawParameters, data.meta.page, data.meta.pageSize])

  useEffect(() => {
    Analytics.pageView('/institution-badge')
  }, [])

  useEffect(() => {
    if (user.id) {
      const localParameters = {
        ...parameters,
        state: IN_INSTITUTION_STATE,
        period: { startDate: parameters.period.startDate.format(DATE_FORMAT), endDate: parameters.period.endDate.format(DATE_FORMAT) }
      }

      setLoading(true)

      getAllMaccsInternships(user, localParameters).then((json) => {
        setData({ ...json, meta: new Metadata(retrieveMetadata(json.meta)) })
      }).finally(() => setLoading(false))
    }
  }, [user.id, parameters])

  const columns = useMemo(() => [
    {
      title: t('Start'),
      key: getStartDate,
      style: { maxWidth: '100px' }
    },
    {
      title: t('End'),
      key: getEndDate,
      style: { maxWidth: '100px' }
    },
    {
      title: t('School'),
      key: getSchoolName,
      style: { maxWidth: '200px' },
      context: INSTITUTION_MACCS_CONTEXT
    },
    {
      title: t('Institution'),
      key: getInstitutionName,
      style: { maxWidth: '200px' },
      context: SCHOOL_MACCS_CONTEXT
    },
    {
      title: t("Internship's speciality"),
      key: getSpecialityAbbreviation
    },
    {
      title: t('Financial year'),
      key: getAssistantFullFinancialYearAbbreviation
    },
    {
      title: t("Assistant's speciality"),
      key: getAssistantFullSpecialityAbbreviation
    },
    {
      title: t('Working regime'),
      key: getWorkingRegimeName,
      translate: true
    },
    {
      title: t('Lastname'),
      key: getAssistantLastname
    },
    {
      title: t('Firstname'),
      key: getAssistantFirstname
    },
    {
      title: t('Email'),
      key: getAssistantEmail,
      style: { maxWidth: '400px' }
    },
    {
      title: t('Registration number'),
      key: getAssistantMatricule
    },
    {
      title: t('Phone'),
      key: getAssistantPhone
    },
    {
      title: t('Birthdate'),
      key: getAssistantBirthdate
    }
  ], [t])

  const exportData = format => {
    const exportParams = {
      startDate: parameters.period.startDate.format(DATE_FORMAT),
      endDate: parameters.period.endDate.format(DATE_FORMAT),
      language: user.language ?? LanguageLocales.EN,
      format
    }

    exportMaccsInternshipsByPeriod(user, exportParams).then(json => {
      getDocument(
        json.job,
        user,
        () => onError(t('An error has occured during the exported file retrieving'))
      )
    }).catch(response => {
      onError(t('An error has occured during the export'))
    })
  }

  return (
    <div className='internships-reporting-container' style={{ height: maxHeight }}>
      <DataTable
        actions={[]}
        columns={columns}
        data={data.data}
        display={{ period: true }}
        filters={{}}
        loading={loading}
        metadata={data.meta}
        parameters={parameters}
        onExport={exportData}
        onParametersChange={parameters => {
          const newParameters = new TableParameters({
            ...parameters,
            period: parameters.period.startDate && parameters.period.endDate
              ? { startDate: parameters.period.startDate.format(DATE_FORMAT), endDate: parameters.period.endDate.format(DATE_FORMAT) }
              : INITIAL_PERIOD
          })

          if (rawParameters.period.startDate !== newParameters.period.startDate || rawParameters.period.endDate !== newParameters.period.endDate || rawParameters.search !== newParameters.search) {
            newParameters.page = 1
          }

          setRawParameters(newParameters)
        }}
      />
    </div>
  )
}

export default AssistantsReportingView
